import { Injectable } from '@angular/core';
import { ICampaignSponsorshipItem } from '@fmnts/api/next';
import { Decimal } from '@fmnts/core/decimal';
import {
  AbstractGraphNodeBuilder,
  GraphBuilderContext,
  OperatorNodeDefinition,
} from '@fmnts/declarative-forms';
import {
  FieldGraphNodeBuilder,
  FieldOperatorName,
  FormGraphNodeBuilder,
} from '@fmnts/declarative-forms/common';
import { declarativeFormFieldPath } from '@formunauts/shared/declarative-forms/domain';
import { filter, map, OperatorFunction } from 'rxjs';
import { IntervalAmount } from '../donation-amount/donation-amount.service';

export enum SponsorshipNodeName {
  ItemChanges = 'sponsorship.item_changes',
  ValueChanges = 'sponsorship.value_changes',
}

export enum SponsorshipOperatorName {
  FilterSetMinAmount = 'sponsorship.filter.set_min_amount',
  MapItemToIntervalAmount = 'sponsorship.map.item.interval_amount',
}

type SponsorshipAmountProp = 'min_amount';

/**
 * Builder for graph nodes related to sponsorships.
 */
@Injectable({
  providedIn: 'root',
})
export class SponsorshipGraphNodeBuilder extends AbstractGraphNodeBuilder<
  SponsorshipNodeName,
  SponsorshipOperatorName
> {
  constructor(
    private fieldBuilder: FieldGraphNodeBuilder,
    private formBuilder: FormGraphNodeBuilder,
  ) {
    super();

    this.addNode(SponsorshipNodeName.ValueChanges, (_, ctx) =>
      this.valueChanges$(ctx),
    ).addNode(SponsorshipNodeName.ItemChanges, (_, ctx) =>
      this.itemChanges$(ctx),
    );

    this.addOperator(
      SponsorshipOperatorName.FilterSetMinAmount,
      (def: OperatorNodeDefinition<[boolean?]>) =>
        this.filterSetMinAmount(def.args?.[0]),
    ).addOperator(
      SponsorshipOperatorName.MapItemToIntervalAmount,
      (def: OperatorNodeDefinition<[SponsorshipAmountProp]>) =>
        def.args?.[0] ? this.mapItemToIntervalAmount(def.args?.[0]) : undefined,
    );
  }

  /**
   * @param ctx Builder context
   *
   * @returns
   * An `Observable` that emits with changes to the
   * selected sponsorship value
   */
  private valueChanges$(ctx: GraphBuilderContext) {
    return this.formBuilder.fieldValueChanges$(
      { path: declarativeFormFieldPath.sponsorship },
      ctx,
    );
  }

  /**
   * @param ctx Builder context
   *
   * @returns
   * An `Observable` that emits with changes to the
   * selected sponsorship item.
   */
  private itemChanges$(ctx: GraphBuilderContext) {
    return this.valueChanges$(ctx).pipe(
      this.fieldBuilder.mapToSelectedItem(
        {
          operator: FieldOperatorName.MapSelectedItem,
          args: [{ path: declarativeFormFieldPath.sponsorship }],
        },
        ctx,
      ) as OperatorFunction<unknown, ICampaignSponsorshipItem>,
    );
  }

  /**
   * @param expected Expected value for `set_min_amount`
   *
   * @returns
   * Filters emissions from a source `Observable` that
   * emits with a sponsorship item where the property
   * `set_min_amount` matches `expected`.
   */
  private filterSetMinAmount(expected = true) {
    return filter<ICampaignSponsorshipItem>(
      (item) => item.set_min_amount === expected,
    );
  }

  /**
   * @returns
   * Maps emissions of sponsorship items and maps them to an
   * interval amount
   */
  private mapItemToIntervalAmount(prop: SponsorshipAmountProp) {
    return map<ICampaignSponsorshipItem, IntervalAmount>((item) => ({
      amount: new Decimal(item[prop] ?? 0),
      base: item.calculation_base,
    }));
  }
}
